import { Link } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { Mail } from 'lucide-react';
import { NewsletterCTA } from '@/components/NewsletterCTA';

export const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary text-primary-foreground mt-24">
      <div className="container mx-auto px-6 md:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 md:gap-10">
          {/* Brand */}
          <div>
            <Link 
              to="/" 
              className="font-serif text-2xl md:text-3xl font-medium hover:opacity-80 transition-opacity duration-500 tracking-wide"
            >
              Modern Nomad Living
            </Link>
            <p className="mt-6 text-primary-foreground/60 text-sm leading-relaxed font-light max-w-xs">
              {t('footer.tagline')}
            </p>
            <a 
              href="mailto:hello@modernnomadliving.com"
              className="mt-4 inline-flex items-center gap-2 text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
            >
              <Mail className="w-4 h-4" />
              hello@modernnomadliving.com
            </a>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-[10px] tracking-luxury uppercase font-normal text-primary-foreground/50 mb-6">Categories</h3>
            <nav className="flex flex-col gap-3" aria-label="Category links">
              <Link 
                to="/category/mobile-living"
                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
              >
                {t('nav.mobileLiving')}
              </Link>
              <Link 
                to="/category/off-grid-homes"
                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
              >
                {t('nav.offGridHomes')}
              </Link>
              <Link 
                to="/category/boat-living"
                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
              >
                {t('nav.boatLiving')}
              </Link>
              <Link 
                to="/category/camping"
                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
              >
                {t('nav.camping')}
              </Link>
            </nav>
          </div>

          {/* Start Here Guides */}
          <div>
            <h3 className="text-[10px] tracking-luxury uppercase font-normal text-primary-foreground/50 mb-6">Start Here</h3>
            <nav className="flex flex-col gap-3" aria-label="Hub guide links">
              <Link 
                to="/article/van-life-basics-complete-guide"
                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
              >
                Van Life Guide
              </Link>
              <Link 
                to="/article/off-grid-living-basics-complete-guide"
                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
              >
                Off-Grid Guide
              </Link>
              <Link 
                to="/article/boat-living-basics-complete-guide"
                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
              >
                Boat Living Guide
              </Link>
              <Link 
                to="/article/camping-basics-complete-guide"
                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
              >
                Camping Guide
              </Link>
            </nav>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-[10px] tracking-luxury uppercase font-normal text-primary-foreground/50 mb-6">Resources</h3>
            <nav className="flex flex-col gap-3" aria-label="Resource links">
              <Link 
                to="/glossary"
                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
              >
                {t('nav.glossary')}
              </Link>
              <Link 
                to="/tools"
                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
              >
                {t('nav.tools')}
              </Link>
              <Link 
                to="/about"
                className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light"
              >
                {t('about.title')}
              </Link>
            </nav>
          </div>

          {/* Newsletter */}
          <div>
            <NewsletterCTA variant="compact" />
          </div>
        </div>

        {/* Legal links row */}
        <div className="border-t border-primary-foreground/10 mt-12 pt-8">
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <Link to="/contact" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light">{t('nav.contact')}</Link>
            <Link to="/privacy-policy" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light">{t('nav.privacyPolicy')}</Link>
            <Link to="/terms" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light">{t('nav.terms')}</Link>
            <Link to="/cookie-policy" className="text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors duration-500 font-light">Cookie Policy</Link>
          </div>
          <p className="text-xs text-primary-foreground/40 text-center max-w-2xl mx-auto leading-relaxed font-light">
            {t('footer.affiliate')}
          </p>
          <p className="text-[10px] tracking-luxury uppercase text-primary-foreground/30 text-center mt-6">
            © {currentYear} Modern Nomad Living · {t('footer.rights')}
          </p>
        </div>
      </div>
    </footer>
  );
};
